import type { BaseOptionType } from "antd/lib/select"
import type { TFunction } from "i18next"

import { CREATED_DATE_PRESETS } from "../../constants/created.ts"
import { isArchivingStatus } from "../../constants/project-statuses.ts"
import { scheduleFilterLabel } from "../../constants/schedule-health.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"
import type { ScheduleState } from "../../types/types.ts"

export interface FilterChip {
  key: string
  label: string
  /** The patch that removes this chip, and only this chip. */
  remove: Partial<ProjectFilters>
}

type MultiKey =
  | "project_status"
  | "project_manager"
  | "project_type"
  | "schedule_health"

const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === "string" && value) return value.split(",")
  return []
}

const labelFrom = (options: BaseOptionType[] | undefined, value: string) =>
  String(
    options?.find((option) => String(option.value) === value)?.label ?? value
  )

/**
 * One chip per selected value, so a single status can be dropped without
 * clearing the others. Values arrive as the stored array or, after a reload of
 * an older persisted slice, as a comma-joined string.
 */
export function activeFilterChips(
  filters: ProjectFilters | null | undefined,
  filterOptions: Record<string, BaseOptionType[]>,
  t: TFunction
): FilterChip[] {
  if (!filters) return []
  const chips: FilterChip[] = []

  const multi = (
    key: MultiKey,
    title: string,
    labelOf: (value: string) => string
  ) => {
    const values = asList(filters[key])
    for (const value of values) {
      const rest = values.filter((other) => other !== value)
      chips.push({
        key: `${key}:${value}`,
        label: `${title}: ${labelOf(value)}`,
        remove: { [key]: rest.length ? rest : null }
      })
    }
  }

  multi("project_status", t("projectsCommand.filters.status"), (value) =>
    labelFrom(filterOptions?.project_status, value)
  )
  multi(
    "project_manager",
    t("projectsCommand.filters.projectManager"),
    (value) => labelFrom(filterOptions?.project_manager, value)
  )
  multi("project_type", t("projectsCommand.filters.projectType"), (value) =>
    labelFrom(filterOptions?.project_type_ids, value)
  )
  multi("schedule_health", t("projectsCommand.filters.schedule"), (value) =>
    scheduleFilterLabel(t, value as ScheduleState)
  )

  if (filters.customer_name) {
    chips.push({
      key: "customer_name",
      label: `${t("projectsCommand.filters.customer")}: ${filters.customer_name}`,
      remove: { customer_name: null }
    })
  }
  if (filters.end_date_after || filters.end_date_before) {
    chips.push({
      key: "end_date",
      label: `${t("projectsCommand.filters.dueDate")}: ${[
        filters.end_date_after,
        filters.end_date_before
      ]
        .map((day) => day || "…")
        .join(" – ")}`,
      remove: { end_date_after: null, end_date_before: null }
    })
  }
  if (
    filters.created_window ||
    filters.created_after ||
    filters.created_before
  ) {
    chips.push({
      key: "created",
      label: `${t("projectsCommand.filters.created")}: ${
        filters.created_window
          ? (CREATED_DATE_PRESETS.find((p) => p.key === filters.created_window)
              ?.label ?? filters.created_window)
          : [filters.created_after, filters.created_before]
              .map((day) => day || "…")
              .join(" – ")
      }`,
      remove: {
        created_window: null,
        created_after: null,
        created_before: null
      }
    })
  }

  return chips.filter(
    (chip) =>
      !(
        chip.key.startsWith("project_status:") &&
        isArchivingStatus(chip.key.split(":")[1])
      )
  )
}

/** Everything the toolbar can set, back to unset. Search, scope and the quick
 *  view are left alone: Clear all clears filters, not where the user is. */
export const CLEARED_FILTERS: Partial<ProjectFilters> = {
  project_status: null,
  project_manager: null,
  project_type: null,
  schedule_health: null,
  customer_name: null,
  customer: null,
  end_date_after: null,
  end_date_before: null,
  created_window: null,
  created_after: null,
  created_before: null
}
